(function () {
  "use strict";

  const PAGE_SIZE = 20;
  const SELECTOR = "[data-flowrods-reviews-import][data-product-id][data-reviews-url]";
  const feeds = new Map();
  const mounted = new WeakMap();

  function mediaUrl(value, mediaBaseUrl) {
    if (typeof value !== "string") return "";
    try {
      let url;
      if (/^flowcat-review-media-[a-f0-9]{24}(?:-\d+)?\.(?:jpg|mp4)$/.test(value)) {
        const base = new URL(typeof mediaBaseUrl === "string" && mediaBaseUrl.startsWith("//") ? `https:${mediaBaseUrl}` : mediaBaseUrl);
        if (base.protocol !== "https:" || base.username || base.password || !base.pathname.endsWith("/")) return "";
        url = new URL(value, base);
      } else {
        url = new URL(value);
      }
      return url.protocol === "https:" || url.protocol === "http:" ? url.href : "";
    } catch {
      return "";
    }
  }

  function selectReviews(data, productId, mediaBaseUrl) {
    if (data?.version !== 1 || !data.products || !data.groups) {
      throw new Error("Invalid reviews feed");
    }
    const groups = Object.hasOwn(data.products, String(productId)) ? data.products[String(productId)] : [];
    const reviews = new Map();
    for (const group of Array.isArray(groups) ? groups : []) {
      const rows = Object.hasOwn(data.groups, group) ? data.groups[group] : [];
      for (const row of Array.isArray(rows) ? rows : []) {
        const id = String(row?.id ?? "").trim();
        const rating = Number(row?.rating);
        if (!id || reviews.has(id) || !Number.isInteger(rating) || rating < 1 || rating > 5) continue;
        const images = [...new Set((Array.isArray(row.images) ? row.images : []).map((value) => mediaUrl(value, mediaBaseUrl)).filter(Boolean))];
        const videos = (Array.isArray(row.videos) ? row.videos : [])
          .map((video) => ({ url: mediaUrl(video?.url, mediaBaseUrl), poster: mediaUrl(video?.poster, mediaBaseUrl) }))
          .filter((video) => video.url);
        const date = typeof row.date === "string" ? row.date : "";
        reviews.set(id, {
          id,
          author: typeof row.author === "string" && row.author.trim() ? row.author.trim() : "Anonymous buyer",
          rating,
          date,
          timestamp: Number.isFinite(Date.parse(date)) ? Date.parse(date) : 0,
          text: typeof row.text === "string" ? row.text : "",
          images,
          videos,
          verifiedPurchase: row.verifiedPurchase === true,
        });
      }
    }
    return [...reviews.values()];
  }

  function summarize(reviews) {
    const counts = [0, 0, 0, 0, 0];
    let total = 0;
    for (const review of reviews) {
      counts[review.rating - 1] += 1;
      total += review.rating;
    }
    return { count: reviews.length, average: reviews.length ? total / reviews.length : 0, counts };
  }

  function filterReviews(reviews, filters) {
    return reviews.filter((review) =>
      (!filters.rating || review.rating === Number(filters.rating)) &&
      (!filters.media || review.images.length || review.videos.length)
    ).sort((left, right) => filters.sort === "oldest"
      ? left.timestamp - right.timestamp
      : right.timestamp - left.timestamp);
  }

  function element(doc, tag, className, text) {
    const node = doc.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function icon(root, name) {
    const source = root.querySelector(`[data-review-${name}]`)?.content?.firstElementChild;
    return source ? source.cloneNode(true) : element(root.ownerDocument, "span", "icon", name === "star" ? "\u2605" : "\u2713");
  }

  function stars(root, rating, className) {
    const doc = root.ownerDocument;
    const container = element(doc, "div", className);
    container.setAttribute("role", "img");
    container.setAttribute("aria-label", `${rating.toFixed(1)} out of 5 stars`);
    for (let index = 0; index < 5; index += 1) {
      const star = element(doc, "span", "flowrods-review-star");
      star.setAttribute("aria-hidden", "true");
      const base = element(doc, "span", "flowrods-review-star__base");
      const fill = element(doc, "span", "flowrods-review-star__fill");
      fill.style.width = `${Math.max(0, Math.min(1, rating - index)) * 100}%`;
      base.append(icon(root, "star"));
      fill.append(icon(root, "star"));
      star.append(base, fill);
      container.append(star);
    }
    return container;
  }

  function createReviewCard(root, review, openImages) {
    const doc = root.ownerDocument;
    const card = element(doc, "article", "flowrods-review-card");
    let media;
    if (review.images.length || review.videos.length) {
      media = element(doc, "div", "flowrods-review-card__media flowrods-review-card__media--imported");
      review.images.forEach((url, index) => {
        const button = element(doc, "button", "flowcat-review-media-button");
        button.type = "button";
        button.setAttribute("aria-label", `View review photo ${index + 1} by ${review.author}`);
        button.title = "View photo";
        const image = element(doc, "img");
        image.src = url;
        image.alt = `Review photo ${index + 1}`;
        image.loading = "lazy";
        image.decoding = "async";
        image.addEventListener("error", () => {
          button.hidden = true;
        }, { once: true });
        button.append(image);
        button.addEventListener("click", () => openImages(review.images, index, button));
        media.append(button);
      });
      for (const item of review.videos) {
        const video = element(doc, "video", "flowrods-review-video");
        video.controls = true;
        video.playsInline = true;
        video.preload = "metadata";
        video.src = item.url;
        if (item.poster) video.poster = item.poster;
        video.setAttribute("aria-label", `Review video by ${review.author}`);
        media.append(video);
      }
    }
    const content = element(doc, "div", "flowrods-review-card__content");
    const identity = element(doc, "div", "flowrods-review-card__identity");
    identity.append(element(doc, "strong", "", review.author));
    if (review.verifiedPurchase) {
      const verified = element(doc, "span", "flowrods-review-card__verified");
      verified.setAttribute("role", "img");
      verified.setAttribute("aria-label", "Verified purchase");
      verified.title = "Verified purchase";
      verified.append(icon(root, "check"));
      identity.append(verified);
    }
    content.append(identity);
    if (review.date) {
      const date = element(doc, "time", "flowrods-review-card__date");
      if (review.timestamp) {
        date.dateTime = new Date(review.timestamp).toISOString();
        date.textContent = new Intl.DateTimeFormat(doc.documentElement.lang || "en", {
          year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
        }).format(review.timestamp);
      } else {
        date.textContent = review.date;
      }
      content.append(date);
    }
    content.append(stars(root, review.rating, "flowrods-review-card__rating"));
    if (review.text.trim()) {
      const text = element(doc, "div", "flowrods-review-card__text richtext");
      text.append(element(doc, "p", "flowrods-review-import-text", review.text));
      content.append(text);
    }
    if (media) content.append(media);
    card.append(content);
    return card;
  }

  function createLightbox(root) {
    const doc = root.ownerDocument;
    const dialog = element(doc, "dialog", "flowrods-review-lightbox");
    dialog.setAttribute("aria-label", "Review photos");
    const close = element(doc, "button", "flowrods-review-lightbox__close", "\u00d7");
    close.type = "button";
    close.title = "Close photo";
    close.setAttribute("aria-label", "Close photo");
    const previous = element(doc, "button", "flowrods-review-lightbox__previous", "\u2039");
    const next = element(doc, "button", "flowrods-review-lightbox__next", "\u203a");
    for (const [button, label] of [[previous, "Previous photo"], [next, "Next photo"]]) {
      button.type = "button";
      button.title = label;
      button.setAttribute("aria-label", label);
    }
    const image = element(doc, "img");
    const count = element(doc, "span", "flowrods-review-lightbox__count");
    count.setAttribute("aria-live", "polite");
    dialog.append(close, previous, image, next, count);
    root.append(dialog);
    let images = [];
    let index = 0;
    let trigger;
    const update = () => {
      image.src = images[index];
      image.alt = `Review photo ${index + 1} of ${images.length}`;
      count.textContent = `${index + 1} / ${images.length}`;
      previous.hidden = images.length < 2;
      next.hidden = images.length < 2;
    };
    close.addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => trigger?.focus());
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
    const move = (direction) => {
      index = (index + direction + images.length) % images.length;
      update();
    };
    previous.addEventListener("click", () => move(-1));
    next.addEventListener("click", () => move(1));
    dialog.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        event.preventDefault();
        move(event.key === "ArrowLeft" ? -1 : 1);
      }
    });
    return (urls, selected, button) => {
      images = urls;
      index = selected;
      trigger = button;
      update();
      dialog.showModal();
    };
  }

  function selectControl(doc, label, options) {
    const wrapper = element(doc, "label", "flowrods-review-filter");
    wrapper.append(element(doc, "span", "", label));
    const select = element(doc, "select");
    for (const [value, text] of options) {
      const option = element(doc, "option", "", text);
      option.value = value;
      select.append(option);
    }
    wrapper.append(select);
    return { wrapper, select };
  }

  function render(root, reviews) {
    const doc = root.ownerDocument;
    const number = new Intl.NumberFormat(doc.documentElement.lang || "en");
    const stats = summarize(reviews);
    let summary = root.querySelector(".flowrods-reviews__summary");
    const grid = root.querySelector(".flowrods-reviews__grid");
    if (!grid) throw new Error("Missing review grid");
    if (!summary) {
      summary = element(doc, "div", "flowrods-reviews__summary");
      grid.before(summary);
    }
    let score = summary.querySelector(".flowrods-reviews__score");
    if (!score) {
      score = element(doc, "div", "flowrods-reviews__score");
      summary.prepend(score);
    }
    score.replaceChildren(
      stars(root, stats.average, "flowrods-reviews__summary-stars"),
      element(doc, "strong", "flowrods-review-average", stats.average.toFixed(1)),
      element(doc, "span", "flowrods-reviews__count", `${number.format(stats.count)} ${stats.count === 1 ? "review" : "reviews"}`)
    );
    const toolbar = element(doc, "div", "flowrods-review-toolbar");
    const ratings = selectControl(doc, "Rating", [["", "All ratings"], ...[5, 4, 3, 2, 1].map((rating) => [String(rating), `${rating} ${rating === 1 ? "star" : "stars"}`])]);
    const sort = selectControl(doc, "Sort", [["newest", "Newest"], ["oldest", "Oldest"]]);
    const mediaLabel = element(doc, "label", "flowcat-review-media-filter");
    const media = element(doc, "input");
    media.type = "checkbox";
    mediaLabel.append(media, element(doc, "span", "", "With photos or video"));
    toolbar.append(ratings.wrapper, sort.wrapper, mediaLabel);
    grid.before(toolbar);

    const distribution = element(doc, "details", "flowrods-review-distribution");
    distribution.append(element(doc, "summary", "", "Rating breakdown"));
    const bars = element(doc, "div", "flowrods-review-distribution__bars");
    for (let rating = 5; rating >= 1; rating -= 1) {
      const count = stats.counts[rating - 1];
      const row = element(doc, "button", "flowrods-review-distribution__row");
      row.type = "button";
      row.setAttribute("aria-label", `${rating} ${rating === 1 ? "star" : "stars"}: ${number.format(count)} reviews`);
      const meter = element(doc, "meter");
      meter.min = 0;
      meter.max = Math.max(1, stats.count);
      meter.value = count;
      meter.setAttribute("aria-hidden", "true");
      row.append(element(doc, "span", "", `${rating} ${rating === 1 ? "star" : "stars"}`), meter, element(doc, "span", "", number.format(count)));
      row.addEventListener("click", () => {
        ratings.select.value = String(rating);
        page = 1;
        update();
      });
      bars.append(row);
    }
    distribution.append(bars);
    toolbar.before(distribution);

    const status = element(doc, "p", "flowrods-review-results");
    status.setAttribute("role", "status");
    status.setAttribute("aria-live", "polite");
    grid.before(status);
    const pagination = element(doc, "nav", "flowrods-review-pagination");
    pagination.setAttribute("aria-label", "Review pages");
    const previous = element(doc, "button", "", "\u2190");
    const next = element(doc, "button", "", "\u2192");
    for (const [button, label] of [[previous, "Previous reviews"], [next, "Next reviews"]]) {
      button.type = "button";
      button.title = label;
      button.setAttribute("aria-label", label);
    }
    const pageLabel = element(doc, "span");
    pagination.append(previous, pageLabel, next);
    grid.after(pagination);
    const openImages = createLightbox(root);
    let page = 1;
    const update = () => {
      for (const video of grid.querySelectorAll("video")) video.pause();
      const matching = filterReviews(reviews, { rating: ratings.select.value, media: media.checked, sort: sort.select.value });
      const pages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE));
      page = Math.min(page, pages);
      const start = (page - 1) * PAGE_SIZE;
      grid.replaceChildren(...matching.slice(start, start + PAGE_SIZE).map((review) => createReviewCard(root, review, openImages)));
      if (!matching.length) grid.append(element(doc, "p", "flowrods-review-empty", "No reviews match your selection."));
      status.textContent = matching.length ? `${number.format(start + 1)}\u2013${number.format(Math.min(start + PAGE_SIZE, matching.length))} of ${number.format(matching.length)} reviews` : "0 reviews";
      pageLabel.textContent = `Page ${page} of ${pages}`;
      previous.disabled = page === 1;
      next.disabled = page === pages;
      pagination.hidden = pages < 2;
    };
    for (const control of [ratings.select, sort.select, media]) {
      control.addEventListener("change", () => {
        page = 1;
        update();
      });
    }
    for (const [button, direction] of [[previous, -1], [next, 1]]) {
      button.addEventListener("click", () => {
        page += direction;
        update();
        status.scrollIntoView({ block: "nearest" });
      });
    }
    update();
  }

  function loadFeed(url) {
    if (!feeds.has(url)) {
      const request = fetch(url, { credentials: "omit" }).then((response) => {
        if (!response.ok) throw new Error("Reviews unavailable");
        return response.json();
      }).catch((error) => {
        feeds.delete(url);
        throw error;
      });
      feeds.set(url, request);
    }
    return feeds.get(url);
  }

  async function mount(root) {
    if (mounted.has(root)) return;
    mounted.set(root, true);
    const status = element(root.ownerDocument, "p", "flowrods-review-load-status", "Loading reviews...");
    status.setAttribute("role", "status");
    const grid = root.querySelector(".flowrods-reviews__grid");
    if (!grid) return;
    grid.before(status);
    root.setAttribute("aria-busy", "true");
    try {
      const data = await loadFeed(root.dataset.reviewsUrl);
      if (!root.isConnected) return;
      render(root, selectReviews(data, root.dataset.productId, root.dataset.reviewMediaBase));
      status.remove();
    } catch {
      status.textContent = "Reviews could not be loaded. ";
      const retry = element(root.ownerDocument, "button", "flowrods-review-retry", "Retry");
      retry.type = "button";
      retry.addEventListener("click", () => {
        status.remove();
        feeds.delete(root.dataset.reviewsUrl);
        mounted.delete(root);
        mount(root);
      }, { once: true });
      status.append(retry);
    } finally {
      root.removeAttribute("aria-busy");
    }
  }

  function boot(scope) {
    if (scope.matches?.(SELECTOR)) mount(scope);
    scope.querySelectorAll(SELECTOR).forEach(mount);
  }

  if (typeof module === "object" && module.exports) {
    module.exports = { selectReviews, summarize, filterReviews, createReviewCard, PAGE_SIZE };
  } else if (!window.FlowrodsReviewImport) {
    window.FlowrodsReviewImport = { boot };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => boot(document), { once: true });
    } else {
      boot(document);
    }
    document.addEventListener("shopify:section:load", (event) => boot(event.target));
  }
})();
